import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { batchesApi } from "@/app/lib/api/batches";
import { Batch, BatchInsert, BatchUpdate, EntityShortFormat, TimelineHealth } from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";
import { createClient } from "@/app/utils/supabase/client";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useAuth } from "@/app/lib/contexts/AuthContext";
import { followUpQueryKeys } from "../queries/useFollowUps";
import toast from "react-hot-toast";

export const batchQueryKeys = {
  all: ["batches"] as const,
  byCompany: (companyId: string) => [...batchQueryKeys.all, "company", companyId] as const,
  byProject: (projectId: string) => [...batchQueryKeys.all, "project", projectId] as const,
  byOrder: (orderId: string) => [...batchQueryKeys.all, "order", orderId] as const,
  detail: (batchId: string) => [...batchQueryKeys.all, "detail", batchId] as const,
  shortFormat: (companyId: string) => [...batchQueryKeys.all, "short-format", companyId] as const,
};

/**
 * Hook to fetch all batches for the current company
 */
export function useBatches() {
  const queryClient = useQueryClient();
  const { selectedCompanyId } = useAppStore();
  const { user } = useAuth();

  const query = useQuery<Batch[]>({
    queryKey: batchQueryKeys.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return batchesApi.getAllBatches(selectedCompanyId);
    },
    enabled: !!selectedCompanyId && !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!selectedCompanyId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`batches-${selectedCompanyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "batches",
          filter: `company_id=eq.${selectedCompanyId}`,
        },
        (payload: RealtimePostgresChangesPayload<Batch>) => {
          // Handle different events
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            queryClient.invalidateQueries({ queryKey: batchQueryKeys.byCompany(selectedCompanyId) });
          } else if (payload.eventType === "DELETE") {
            // Remove from cache
            queryClient.setQueryData<Batch[]>(
              batchQueryKeys.byCompany(selectedCompanyId),
              (old) => old?.filter((b) => b.id !== payload.old?.id) || []
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCompanyId, queryClient]);

  return query;
}

/**
 * Hook to fetch all batches for a project
 */
export function useBatchesByProject(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const query = useQuery<Batch[]>({
    queryKey: batchQueryKeys.byProject(projectId || ""),
    queryFn: () => {
      if (!projectId) throw new Error("No project ID provided");
      return batchesApi.getBatches(projectId);
    },
    enabled: !!projectId && !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!projectId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`batches-${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "batches",
          filter: `project_id=eq.${projectId}`,
        },
        (payload: RealtimePostgresChangesPayload<Batch>) => {
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            queryClient.setQueryData<Batch[]>(
              batchQueryKeys.byProject(projectId),
              (old) => {
                if (!old) return [payload.new as Batch];

                const index = old.findIndex((b) => b.id === payload.new?.id);
                if (index > -1) {
                  const updated = [...old];
                  updated[index] = payload.new as Batch;
                  return updated;
                } else {
                  return [payload.new as Batch, ...old];
                }
              }
            );

            // Also invalidate order-specific queries if needed
            if (payload.new?.order_id) {
              queryClient.invalidateQueries({ 
                queryKey: batchQueryKeys.byOrder(payload.new.order_id) 
              });
            }
          } else if (payload.eventType === "DELETE") {
            queryClient.setQueryData<Batch[]>(
              batchQueryKeys.byProject(projectId),
              (old) => old?.filter((b) => b.id !== payload.old?.id) || []
            );

            // Also invalidate order-specific queries if needed
            if (payload.old?.order_id) {
              queryClient.invalidateQueries({ 
                queryKey: batchQueryKeys.byOrder(payload.old.order_id) 
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, queryClient]);

  return query;
}

/**
 * Hook to fetch batches by order
 */
export function useBatchesByOrder(orderId: string | undefined) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const query = useQuery<Batch[]>({
    queryKey: batchQueryKeys.byOrder(orderId || ""),
    queryFn: () => {
      if (!orderId) throw new Error("No order ID provided");
      return batchesApi.getBatchesByOrder(orderId);
    },
    enabled: !!orderId && !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!orderId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`batches-order-${orderId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "batches",
          filter: `order_id=eq.${orderId}`,
        },
        (payload: RealtimePostgresChangesPayload<Batch>) => {
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            queryClient.setQueryData<Batch[]>(
              batchQueryKeys.byOrder(orderId),
              (old) => {
                if (!old) return [payload.new as Batch];

                const index = old.findIndex((b) => b.id === payload.new?.id);
                if (index > -1) {
                  const updated = [...old];
                  updated[index] = payload.new as Batch;
                  return updated;
                } else {
                  return [payload.new as Batch, ...old];
                }
              }
            );
          } else if (payload.eventType === "DELETE") {
            queryClient.setQueryData<Batch[]>(
              batchQueryKeys.byOrder(orderId),
              (old) => old?.filter((b) => b.id !== payload.old?.id) || []
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId, queryClient]);

  return query;
}

/**
 * Hook to fetch a single batch by ID
 */
export function useBatch(batchId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<Batch>({
    queryKey: batchQueryKeys.detail(batchId || ""),
    queryFn: () => {
      if (!batchId) throw new Error("No batch ID provided");
      return batchesApi.getBatch(batchId);
    },
    enabled: !!batchId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Set up real-time subscription for this specific batch
  useEffect(() => {
    if (!batchId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`batch-${batchId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "batches",
          filter: `id=eq.${batchId}`,
        },
        (payload: RealtimePostgresChangesPayload<Batch>) => {
          queryClient.setQueryData<Batch>(
            batchQueryKeys.detail(batchId),
            payload.new as Batch
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [batchId, queryClient]);

  return query;
}

/**
 * Hook for batch mutations
 */
export function useBatchMutations(projectId: string) {
  const queryClient = useQueryClient();
  const { selectedCompanyId } = useAppStore();

  const createMutation = useMutation({
    mutationFn: async (batch: Omit<BatchInsert, "company_id">) => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return batchesApi.createBatch({
        ...batch,
        company_id: selectedCompanyId,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: batchQueryKeys.byProject(projectId) });
      if (data.order_id) {
        queryClient.invalidateQueries({ queryKey: batchQueryKeys.byOrder(data.order_id) });
      }
      toast.success("Batch created successfully");
    },
    onError: (error) => {
      console.error("Error creating batch:", error);
      toast.error("Failed to create batch");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ batchId, updates }: { batchId: string; updates: BatchUpdate }) =>
      batchesApi.updateBatch(batchId, updates),
    onSuccess: (data, { batchId }) => {
      queryClient.invalidateQueries({ queryKey: batchQueryKeys.detail(batchId) });
      queryClient.invalidateQueries({ queryKey: batchQueryKeys.byProject(projectId) });
      if (data?.order_id) {
        queryClient.invalidateQueries({ queryKey: batchQueryKeys.byOrder(data.order_id) });
      }
      toast.success("Batch updated successfully");
    },
    onError: (error) => {
      console.error("Error updating batch:", error);
      toast.error("Failed to update batch");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (batchId: string) => batchesApi.deleteBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchQueryKeys.byProject(projectId) });
      toast.success("Batch deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting batch:", error);
      toast.error("Failed to delete batch");
    },
  });

  return {
    createBatch: createMutation.mutate,
    updateBatch: updateMutation.mutate,
    deleteBatch: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook for batch service (replaces the class-based service)
 */
export function useBatchService(projectId: string) {
  const { createBatch, updateBatch, deleteBatch } = useBatchMutations(projectId);

  return {
    async saveBatch(batch: { id?: string } & Partial<Batch>) {
      if (batch.id) {
        const { id, ...updates } = batch;
        updateBatch({ batchId: id, updates });
      } else {
        createBatch(batch as Omit<BatchInsert, "company_id">);
      }
    },
    deleteBatch,
  };
}

/**
 * Hook to fetch batches in short format for entity selection
 */
export function useBatchesShortFormat(options?: { enabled?: boolean }) {
  const { selectedCompanyId } = useAppStore();
  const { user } = useAuth();
  const enabled = options?.enabled ?? true;

  return useQuery<EntityShortFormat[]>({
    queryKey: batchQueryKeys.shortFormat(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return batchesApi.getBatchesShortFormat(selectedCompanyId);
    },
    enabled: !!selectedCompanyId && !!user && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to update batch timeline health status
 */
export function useUpdateBatchTimelineHealth() {
  const queryClient = useQueryClient();
  const { selectedCompanyId } = useAppStore();

  return useMutation({
    mutationFn: ({
      batchId,
      health,
      reason
    }: {
      batchId: string;
      health: TimelineHealth;
      reason?: string | null;
    }) =>
      batchesApi.updateBatch(batchId, {
        timeline_health: health,
        timeline_health_reason: reason,
        timeline_health_updated_at: new Date().toISOString()
      }),
    onSuccess: (data) => {
      // Invalidate batch queries
      queryClient.invalidateQueries({
        queryKey: batchQueryKeys.detail(data.id)
      });

      if (selectedCompanyId) {
        queryClient.invalidateQueries({
          queryKey: batchQueryKeys.byCompany(selectedCompanyId)
        });

        // Invalidate threads/follow-ups queries since timeline health affects thread display
        queryClient.invalidateQueries({
          queryKey: followUpQueryKeys.threads.byCompany(selectedCompanyId)
        });
      }

      if (data.project_id) {
        queryClient.invalidateQueries({
          queryKey: batchQueryKeys.byProject(data.project_id)
        });
      }

      if (data.order_id) {
        queryClient.invalidateQueries({
          queryKey: batchQueryKeys.byOrder(data.order_id)
        });
      }

      toast.success("Timeline health updated successfully");
    },
    onError: (error) => {
      console.error("Error updating batch timeline health:", error);
      toast.error("Failed to update timeline health");
    },
  });
}